import { Cog6ToothIcon, CubeIcon, Squares2X2Icon } from '@heroicons/react/24/outline';

import { AVAILABLE_USE_CASES, familyForPreset } from '~/components/home/use-case-catalog';
import {
  filterNavigationGroups,
  filterNavigationTabs,
  navigationGroups,
  settingsPage,
  type NavigationAccess,
  type NavigationPage
} from '~/components/navigation';
import { matchesTerms } from './match';
import type { SearchContext, SearchResult, SearchSection, SearchTarget } from './types';

/**
 * Destinations one navigation page contributes. A page with tabs contributes
 * only its tabs, since the page itself just redirects to the first of them --
 * offering both would put two rows in the palette that land in the same place.
 */
const destinationsFor = (page: NavigationPage, access: NavigationAccess) => {
  const tabs = filterNavigationTabs(page.tabs ?? [], access);

  if (tabs.length === 0) {
    return [
      {
        id: page.href,
        label: page.name,
        description: page.description,
        to: `/app/${page.href}`,
        keywords: page.keywords
      }
    ];
  }

  return tabs.map((tab) => ({
    id: `${page.href}/${tab.href}`,
    label: `${page.name} › ${tab.name}`,
    description: tab.description,
    to: `/app/${page.href}/${tab.href}`,
    // A tab inherits its page's keywords: someone searching "copilot" should
    // still reach the pages underneath it, not just the page row itself.
    keywords: [...(page.keywords ?? []), ...(tab.keywords ?? [])]
  }));
};

const toResult = (destination: ReturnType<typeof destinationsFor>[number]): SearchResult => ({
  id: destination.id,
  label: destination.label,
  description: destination.description,
  primary: { label: 'Go to', target: { kind: 'route', to: destination.to } }
});

export const pagesSection: SearchSection = {
  key: 'pages',
  title: 'Pages',
  icon: Squares2X2Icon,
  sectionClass: 'static',
  search: ({ terms, access }) =>
    filterNavigationGroups(navigationGroups, access)
      .flatMap((group) => group.pages)
      .flatMap((page) => destinationsFor(page, access))
      .filter((destination) =>
        matchesTerms(
          terms,
          destination.label,
          destination.description,
          ...(destination.keywords ?? [])
        )
      )
      .map(toResult)
};

export const settingsSection: SearchSection = {
  key: 'settings',
  title: 'Settings',
  icon: Cog6ToothIcon,
  sectionClass: 'static',
  search: ({ terms, access }) =>
    destinationsFor(settingsPage, access)
      .filter((destination) =>
        matchesTerms(
          terms,
          destination.label,
          destination.description,
          ...(destination.keywords ?? [])
        )
      )
      .map(toResult)
};

const runTarget = (id: string): SearchTarget => ({
  kind: 'route',
  to: `/app/home?useCase=${encodeURIComponent(id)}`
});

/**
 * The built-in use cases from the Home grid. Running one hands the id back to
 * Home, which opens the same run card the grid tile opens.
 *
 * Matched on the explanation behind the info icon as well as the title, which is
 * where the words agencies type live: ACORD form numbers, "workers comp", "dec
 * page". A title match sorts first so the preset named for the query survives the
 * row limit.
 */
const useCaseRows = ({ terms, access }: SearchContext) =>
  AVAILABLE_USE_CASES.filter((useCase) => access.proPresets || !useCase.pro)
    .flatMap((useCase) => {
      const family = familyForPreset(useCase.preset)?.title;
      const matched = matchesTerms(
        terms,
        useCase.title,
        useCase.description,
        family,
        useCase.about.summary,
        ...useCase.about.examples
      );
      return matched ? [{ useCase, rank: matchesTerms(terms, useCase.title) ? 0 : 1 }] : [];
    })
    .sort((a, b) => a.rank - b.rank)
    .map(({ useCase }) => ({
      id: useCase.id,
      label: useCase.title,
      description: useCase.description,
      primary: { label: 'Run', target: runTarget(useCase.id) }
    }));

export const useCasesSection: SearchSection = {
  key: 'use-cases',
  title: 'Use cases',
  icon: CubeIcon,
  sectionClass: 'static',
  search: useCaseRows
};
